import { useState } from "react";
import { Mail, Phone, Reply } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Badge, fmtDate } from "@/components/Protected";

const FILTERS = ["new", "replied", "archived", "all"];

function ContactCard({ m, onStatus }) {
  const subject = encodeURIComponent(`Re: ${m.topic || "Your message to MidHq"}`);
  const body = encodeURIComponent(`Hi ${m.name},\n\n\n\n---\nYou wrote on ${fmtDate(m.created_at)}:\n${m.message}`);
  const reply = () => {
    window.location.href = `mailto:${m.email}?subject=${subject}&body=${body}`;
    if (m.status === "new") onStatus(m, "replied");
  };
  return (
    <article className="border border-white/10 bg-[#0d1524] p-5" data-testid={`contact-msg-${m.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-widest text-[var(--gold)]">{m.topic || "General"}</p>
          <h3 className="mt-1 font-display text-xl font-medium">{m.name}{m.company ? <span className="text-slate-400"> · {m.company}</span> : null}</h3>
          <p className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-300">
            <span className="inline-flex items-center gap-1.5 break-all"><Mail className="h-3.5 w-3.5" />{m.email}</span>
            {m.phone && <a href={`tel:${m.phone}`} className="inline-flex items-center gap-1.5 hover:text-white"><Phone className="h-3.5 w-3.5" />{m.phone}</a>}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-mono text-[11px] text-slate-400">{fmtDate(m.created_at)}</span>
          <Badge s={m.status} testid={`contact-status-${m.id}`} />
        </div>
      </div>
      <p className="mt-4 whitespace-pre-wrap text-sm leading-relaxed text-slate-200">{m.message}</p>
      <div className="mt-5 flex flex-wrap gap-2 border-t border-white/10 pt-4">
        <button onClick={reply} data-testid={`contact-reply-${m.id}`} className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-1.5 text-xs font-medium text-black hover:bg-[var(--gold)]">
          <Reply className="h-3.5 w-3.5" /> Reply by email
        </button>
        {m.status !== "replied" && <button onClick={() => onStatus(m, "replied")} data-testid={`contact-mark-replied-${m.id}`} className="rounded-full border border-white/20 px-4 py-1.5 text-xs hover:border-sky-400">Mark replied</button>}
        {m.status !== "archived" ? (
          <button onClick={() => onStatus(m, "archived")} data-testid={`contact-archive-${m.id}`} className="rounded-full border border-white/20 px-4 py-1.5 text-xs hover:border-sky-400">Archive</button>
        ) : (
          <button onClick={() => onStatus(m, "new")} data-testid={`contact-restore-${m.id}`} className="rounded-full border border-white/20 px-4 py-1.5 text-xs hover:border-sky-400">Move to new</button>
        )}
      </div>
    </article>
  );
}

export function ContactInbox({ items, setItems }) {
  const [filter, setFilter] = useState("new");
  const list = filter === "all" ? items : items.filter((m) => m.status === filter);

  const onStatus = async (m, status) => {
    try {
      await api.patch(`/admin/contacts/${m.id}`, { status });
      setItems((prev) => prev.map((x) => (x.id === m.id ? { ...x, status } : x)));
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="contact-inbox">
      <div className="mb-5 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`contact-filter-${f}`}
            className={`rounded-full border px-4 py-1.5 text-xs capitalize ${filter === f ? "border-white bg-white text-black" : "border-white/15 text-slate-300"}`}>
            {f} ({f === "all" ? items.length : items.filter((m) => m.status === f).length})
          </button>
        ))}
      </div>
      <div className="space-y-3">
        {list.length === 0 && <p className="text-sm text-slate-400" data-testid="contact-empty">No messages here.</p>}
        {list.map((m) => <ContactCard key={m.id} m={m} onStatus={onStatus} />)}
      </div>
    </div>
  );
}
